'use client';

import { useEffect, useRef, useState } from 'react';

const STOPS = [
  { fraction: 0.076, index: 0 },
  { fraction: 0.180, index: 1 },
  { fraction: 0.283, index: 2 },
  { fraction: 0.541, index: 3 },
  { fraction: 0.645, index: 4 },
  { fraction: 0.749, index: 5 },
];

const SPEED = 0.00013;
const PAUSE_MS = 700;
const SAMPLES = 1000;

export default function OrderProcess() {
  const pathRef = useRef<SVGPathElement>(null);
  const dotRef = useRef<SVGCircleElement>(null);
  const [activeStep, setActiveStep] = useState(-1);
  const anim = useRef({ f: 0, paused: false, end: 0, next: 0 });

  useEffect(() => {
    const path = pathRef.current;
    const dot = dotRef.current;
    if (!path || !dot) return;

    let id = 0;
    let prev = 0;
    let samples: DOMPoint[] | null = null;

    // Precalcola i punti del percorso una sola volta: leggere la geometria
    // SVG a ogni frame forza il ricalcolo del layout (forced reflow).
    const pointAt = (f: number) => {
      if (!samples) {
        const len = path.getTotalLength();
        samples = Array.from({ length: SAMPLES + 1 }, (_, i) => path.getPointAtLength((i / SAMPLES) * len));
      }
      return samples[Math.min(SAMPLES, Math.round(f * SAMPLES))];
    };

    const moveDot = (f: number) => {
      const pt = pointAt(f);
      dot.setAttribute('cx', String(pt.x));
      dot.setAttribute('cy', String(pt.y));
    };

    const tick = (now: number) => {
      if (!prev) prev = now;
      const dt = now - prev;
      prev = now;
      const s = anim.current;

      if (s.paused) {
        dot.setAttribute('opacity', '0');
        if (now >= s.end) {
          s.paused = false;
          setActiveStep(-1);
          dot.setAttribute('opacity', '0.9');
        }
      } else {
        s.f += SPEED * dt;
        if (s.f >= 1) { s.f -= 1; s.next = 0; }
        if (s.next < STOPS.length && s.f >= STOPS[s.next].fraction) {
          s.f = STOPS[s.next].fraction;
          s.paused = true;
          s.end = now + PAUSE_MS;
          setActiveStep(STOPS[s.next].index);
          s.next++;
        }
      }

      moveDot(s.f);
      id = requestAnimationFrame(tick);
    };

    // L'animazione gira solo mentre la sezione è visibile
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (!id) {
          prev = 0;
          moveDot(anim.current.f);
          id = requestAnimationFrame(tick);
        }
      } else {
        cancelAnimationFrame(id);
        id = 0;
      }
    });
    observer.observe(path);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(id);
    };
  }, []);

  const on = (i: number) => activeStep === i;

  const stepStyle = (x: number, y: number, i: number): React.CSSProperties => ({
    transform: `translate(${x}px,${y}px) scale(${on(i) ? 1.18 : 1}) translate(${-x}px,${-y}px)`,
    transition: 'transform 0.3s ease',
  });

  const t = (ms: string) => ({ transition: `all 0.3s ${ms}` });

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-8 lg:gap-14 items-center">

          {/* SVG */}
          <div className="relative flex justify-center">
            <svg
              viewBox="0 0 440 640"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-auto"
              style={{ maxWidth: 420 }}
            >
              <defs>
                <filter id="dotGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="6" result="b" />
                  <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <filter id="stepGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="5" result="b" />
                  <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>

              {/* Track */}
              <path
                ref={pathRef}
                d="M 110,70 L 110,540 Q 110,570 140,570 L 300,570 Q 330,570 330,540 L 330,70 Q 330,40 300,40 L 140,40 Q 110,40 110,70"
                stroke="#e5e7eb"
                strokeWidth="2.5"
                fill="none"
                strokeDasharray="8 6"
                opacity="0.5"
              />

              {/* Arrows — left down */}
              <path d="M 104,140 l 6,10 6,-10" stroke="#d1d5db" strokeWidth="1.5" fill="none" />
              <path d="M 104,420 l 6,10 6,-10" stroke="#d1d5db" strokeWidth="1.5" fill="none" />
              {/* Arrows — right up */}
              <path d="M 324,440 l 6,-10 6,10" stroke="#d1d5db" strokeWidth="1.5" fill="none" />
              <path d="M 324,240 l 6,-10 6,10" stroke="#d1d5db" strokeWidth="1.5" fill="none" />

              {/* ---- CLIENT (top center) ---- */}
              <g transform="translate(220,40)">
                <rect x="-30" y="-30" width="60" height="60" rx="15" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2" />
                <circle cx="0" cy="-6" r="7.5" stroke="#475569" strokeWidth="1.8" fill="none" />
                <path d="M-12,16 Q-12,3 0,3 Q12,3 12,16" stroke="#475569" strokeWidth="1.8" fill="none" strokeLinecap="round" />
              </g>
              <text x="220" y="90" textAnchor="middle" fill="#475569" fontSize="13" fontWeight="600" fontFamily="inherit">Cliente</text>

              {/* ---- TDS MED (bottom center) ---- */}
              <g transform="translate(220,570)">
                <rect x="-30" y="-30" width="60" height="60" rx="15" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="2" />
                <path d="M-9,-9 L9,-9 L9,9 L-9,9 Z" stroke="#2563eb" strokeWidth="1.8" fill="none" />
                <path d="M-4,-3 L-4,5 M0,-7 L0,5 M4,-3 L4,5 M-6,5 L6,5" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" />
              </g>
              <text x="220" y="620" textAnchor="middle" fill="#2563eb" fontSize="13" fontWeight="600" fontFamily="inherit">TDS Med</text>

              {/* ===== STEP 0 — Upload (left, y=180) ===== */}
              <g style={stepStyle(110, 180, 0)}>
                <circle cx="110" cy="180" r="22" fill={on(0) ? '#eff6ff' : 'white'} stroke={on(0) ? '#2563eb' : '#e5e7eb'} strokeWidth="2" style={t('ease')} />
                {on(0) && <circle cx="110" cy="180" r="26" fill="none" stroke="#2563eb" strokeWidth="2" opacity="0.25" filter="url(#stepGlow)" />}
                <g transform="translate(110,180)">
                  <path d="M-6,3 L0,-6 L6,3 M0,-5 L0,8" stroke={on(0) ? '#2563eb' : '#6b7280'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" style={t('ease')} />
                </g>
              </g>
              <text x="78" y="185" textAnchor="end" fill={on(0) ? '#2563eb' : '#6b7280'} fontSize="13" fontWeight={on(0) ? 600 : 500} style={t('ease')}>Upload</text>

              {/* ===== STEP 1 — Preventivo (left, y=330) ===== */}
              <g style={stepStyle(110, 330, 1)}>
                <circle cx="110" cy="330" r="22" fill={on(1) ? '#eff6ff' : 'white'} stroke={on(1) ? '#2563eb' : '#e5e7eb'} strokeWidth="2" style={t('ease')} />
                {on(1) && <circle cx="110" cy="330" r="26" fill="none" stroke="#2563eb" strokeWidth="2" opacity="0.25" filter="url(#stepGlow)" />}
                <g transform="translate(110,330)">
                  <path d="M-5,-8 L5,-8 L5,8 L-5,8 Z M-3,-5 L3,-5 M-3,-1.5 L3,-1.5 M-3,2 L1,2" stroke={on(1) ? '#2563eb' : '#6b7280'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" style={t('ease')} />
                </g>
              </g>
              <text x="78" y="335" textAnchor="end" fill={on(1) ? '#2563eb' : '#6b7280'} fontSize="13" fontWeight={on(1) ? 600 : 500} style={t('ease')}>Preventivo</text>

              {/* ===== STEP 2 — Conferma (left, y=480) ===== */}
              <g style={stepStyle(110, 480, 2)}>
                <circle cx="110" cy="480" r="22" fill={on(2) ? '#eff6ff' : 'white'} stroke={on(2) ? '#2563eb' : '#e5e7eb'} strokeWidth="2" style={t('ease')} />
                {on(2) && <circle cx="110" cy="480" r="26" fill="none" stroke="#2563eb" strokeWidth="2" opacity="0.25" filter="url(#stepGlow)" />}
                <g transform="translate(110,480)">
                  <path d="M-6,0 L-2,5 L7,-5" stroke={on(2) ? '#2563eb' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" style={t('ease')} />
                </g>
              </g>
              <text x="78" y="485" textAnchor="end" fill={on(2) ? '#2563eb' : '#6b7280'} fontSize="13" fontWeight={on(2) ? 600 : 500} style={t('ease')}>Conferma</text>

              {/* ===== STEP 3 — Produzione (right, y=480) ===== */}
              <g style={stepStyle(330, 480, 3)}>
                <circle cx="330" cy="480" r="22" fill={on(3) ? '#eff6ff' : 'white'} stroke={on(3) ? '#2563eb' : '#e5e7eb'} strokeWidth="2" style={t('ease')} />
                {on(3) && <circle cx="330" cy="480" r="26" fill="none" stroke="#2563eb" strokeWidth="2" opacity="0.25" filter="url(#stepGlow)" />}
                <g transform="translate(330,480)">
                  <rect x="-7" y="-7" width="14" height="14" rx="2" stroke={on(3) ? '#2563eb' : '#6b7280'} strokeWidth="1.8" fill="none" style={t('ease')} />
                  <path d="M-3,0 L0,-4 L3,0 M0,0 L0,5" stroke={on(3) ? '#2563eb' : '#6b7280'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" style={t('ease')} />
                </g>
              </g>
              <text x="362" y="485" textAnchor="start" fill={on(3) ? '#2563eb' : '#6b7280'} fontSize="13" fontWeight={on(3) ? 600 : 500} style={t('ease')}>Produzione</text>

              {/* ===== STEP 4 — Qualità (right, y=330) ===== */}
              <g style={stepStyle(330, 330, 4)}>
                <circle cx="330" cy="330" r="22" fill={on(4) ? '#eff6ff' : 'white'} stroke={on(4) ? '#2563eb' : '#e5e7eb'} strokeWidth="2" style={t('ease')} />
                {on(4) && <circle cx="330" cy="330" r="26" fill="none" stroke="#2563eb" strokeWidth="2" opacity="0.25" filter="url(#stepGlow)" />}
                <g transform="translate(330,330)">
                  <circle cx="-2" cy="-2" r="5.5" stroke={on(4) ? '#2563eb' : '#6b7280'} strokeWidth="1.8" fill="none" style={t('ease')} />
                  <path d="M2,2 L7,7" stroke={on(4) ? '#2563eb' : '#6b7280'} strokeWidth="1.8" strokeLinecap="round" style={t('ease')} />
                </g>
              </g>
              <text x="362" y="335" textAnchor="start" fill={on(4) ? '#2563eb' : '#6b7280'} fontSize="13" fontWeight={on(4) ? 600 : 500} style={t('ease')}>QC</text>

              {/* ===== STEP 5 — Spedizione (right, y=180) ===== */}
              <g style={stepStyle(330, 180, 5)}>
                <circle cx="330" cy="180" r="22" fill={on(5) ? '#eff6ff' : 'white'} stroke={on(5) ? '#2563eb' : '#e5e7eb'} strokeWidth="2" style={t('ease')} />
                {on(5) && <circle cx="330" cy="180" r="26" fill="none" stroke="#2563eb" strokeWidth="2" opacity="0.25" filter="url(#stepGlow)" />}
                <g transform="translate(330,180)">
                  <path d="M-8,-3 L-8,4 L8,4 L8,-1 L4,-1 L2,-3 Z" stroke={on(5) ? '#2563eb' : '#6b7280'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" style={t('ease')} />
                  <circle cx="-4" cy="4" r="2.5" stroke={on(5) ? '#2563eb' : '#6b7280'} strokeWidth="1.5" fill="none" style={t('ease')} />
                  <circle cx="4" cy="4" r="2.5" stroke={on(5) ? '#2563eb' : '#6b7280'} strokeWidth="1.5" fill="none" style={t('ease')} />
                </g>
              </g>
              <text x="362" y="185" textAnchor="start" fill={on(5) ? '#2563eb' : '#6b7280'} fontSize="13" fontWeight={on(5) ? 600 : 500} style={t('ease')}>Spedizione</text>

              {/* ===== ANIMATED DOT ===== */}
              <circle ref={dotRef} r="8" fill="#2563eb" filter="url(#dotGlow)" opacity="0.9" style={{ transition: 'opacity 0.2s ease' }}>
                <animate attributeName="r" values="7;9;7" dur="1.5s" repeatCount="indefinite" />
              </circle>
            </svg>
          </div>

          {/* Text */}
          <div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-secondary-500 mb-6 leading-tight">
              Un processo <span className="text-gradient">lineare</span> per un risultato <span className="text-gradient">ottimale</span>
            </h2>
            <p className="text-secondary-400 leading-relaxed mb-4">
              Il nostro processo è progettato per essere semplice e trasparente.
              Carica il tuo file 3D, ricevi un preventivo istantaneo e conferma l&apos;ordine:
              al resto pensiamo noi.
            </p>
            <p className="text-secondary-400 leading-relaxed mb-8">
              Ogni ordine segue un percorso controllato — dalla verifica del file alla produzione,
              dal controllo qualità alla spedizione — per garantirti parti perfette,
              consegnate nei tempi concordati.
            </p>

            <div className="space-y-4">
              {[
                { num: '01', title: 'Upload e preventivo', desc: 'Carica il file e ricevi un preventivo istantaneo con tecnologie e materiali consigliati.' },
                { num: '02', title: 'Produzione', desc: 'Avviamo la stampa con la tecnologia scelta e monitoriamo ogni fase del processo.' },
                { num: '03', title: 'Controllo e spedizione', desc: 'Ogni pezzo viene verificato prima della spedizione. Consegna in tutta Italia.' },
              ].map((step) => (
                <div key={step.num} className="flex gap-4 items-start">
                  <span className="flex-shrink-0 w-10 h-10 bg-primary-50 border border-primary-200 rounded-xl flex items-center justify-center text-primary-600 font-bold text-sm font-display">
                    {step.num}
                  </span>
                  <div>
                    <h3 className="font-semibold text-secondary-500 mb-0.5">{step.title}</h3>
                    <p className="text-sm text-secondary-400 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
